import { CheckoutItem } from "../../CampaignInterface/NewCampaignInterface";
import { Input } from "@/shadcnComponents/ui/input";
import { Switch } from "@/shadcnComponents/ui/switch";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { Separator } from "@/shadcnComponents/ui/separator";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shadcnComponents/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shadcnComponents/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/shadcnComponents/ui/radio-group";
import { useState } from "react";
import { Label } from "@/shadcnComponents/ui/label";
import { Button } from "@/shadcnComponents/ui/button";
import mixpanel from "mixpanel-browser";
import { useNewCampaign } from "../../CustomHooks/NewCampaignHooks";
import { NoActivePlanAlertButton } from "../Components";

export const NewCampaignCheckOutCard = () => {
  const {
    setSelectedPaymentFormat,
    coupon,
    setCoupon,
    appliedCoup,
    setAppliedCoup,
    onCouponSubmit,
    originalPriceBeforeCoupon,
    setOriginalPriceBeforeCoupon,
    setPreviewData,
    allowCoupons,
    AllCredits,
    usedCollaborationCredits,
    usedAnchorsCredits,
    setUsedAnchorsCredits,
    setUsedCollaborationCredits,
    collaborationCharges,
    campaignCharges,
    setUsedCampaignAddOn,
    usedCampaignAddOn,
    setSelectedPaymentMethod,
    selectedPaymentFormat,
    selectedPaymentMethod,
    selectedCreators,
    previewData,
    handlePayment,
  } = useNewCampaign();

  const selectedCreatorsLength = selectedCreators.length;
  const Amount = previewData?.ExpectedSpendAmount || 0;

  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [finalAmount, setFinalAmount] = useState(0);

  const additionalAmounts =
    collaborationCharges * selectedCreatorsLength +
    usedCampaignAddOn * campaignCharges -
    (usedCollaborationCredits * collaborationCharges + usedAnchorsCredits);

  const subtotal = Amount + additionalAmounts;
  const gstAmount = (subtotal * 0.18)?.toFixed(2);
  const totalAmount = (subtotal + subtotal * 0.18)?.toFixed(2);

  const partialPayment = (subtotal * 0.1 + subtotal * 0.1 * 0.18)?.toFixed(2);

  const [openCoupon, setOpenCoupon] = useState(false);

  // Calculate total charges before credits
  const totalChargesBeforeCredits =
    Amount +
    collaborationCharges * selectedCreatorsLength +
    usedCampaignAddOn * campaignCharges;

  const handleCreditsChange = (
    type: "collaboration" | "anchors",
    value: string
  ) => {
    const numValue = Math.floor(Number(value)) || 0; // Ensure integer value

    if (type === "collaboration") {
      // Handle collaboration credits (1 credit = collaborationCharges)
      const remainingAfterAnchors =
        totalChargesBeforeCredits - usedAnchorsCredits;
      const maxCollabCredits =
        Math.floor(remainingAfterAnchors / collaborationCharges) || 0;

      const maxAllowedCollab = Math.min(
        numValue,
        maxCollabCredits,
        AllCredits.collaborationCredits,
        selectedCreatorsLength
      );
      setUsedCollaborationCredits(maxAllowedCollab);
    } else {
      // Handle anchors credits (1 credit = ₹1)
      const maxAllowedAnchors = Math.min(
        numValue,
        AllCredits.anchorsCredits,
        Math.floor(totalChargesBeforeCredits)
      );
      setUsedAnchorsCredits(maxAllowedAnchors);

      // Recalculate max allowed collaboration credits after applying anchors
      const remainingAfterAnchors =
        totalChargesBeforeCredits - maxAllowedAnchors;
      const maxCollabCredits =
        Math.floor(remainingAfterAnchors / collaborationCharges) || 0;
      setUsedCollaborationCredits(
        Math.min(
          usedCollaborationCredits,
          maxCollabCredits,
          AllCredits.collaborationCredits,
          selectedCreatorsLength
        )
      );
    }
  };

  const checkoutItems: CheckoutItem[] = [
    { label: "Campaign Amount", amount: originalPriceBeforeCoupon || Amount },
    {
      label: "Collaboration Charges",
      amount: selectedCreatorsLength * collaborationCharges,
    },
  ];

  if (AllCredits?.campaignCredits < 1) {
    setUsedCampaignAddOn(1);
    checkoutItems.push({
      label: "Campaign charges",
      amount: campaignCharges,
    });
  }

  if (originalPriceBeforeCoupon - Amount > 0) {
    checkoutItems.push({
      label: "Coupon Discount",
      amount: -(originalPriceBeforeCoupon - Amount),
    });
  }

  if (usedAnchorsCredits > 0) {
    checkoutItems.push({
      label: "Anchors Credits applied",
      amount: -usedAnchorsCredits,
    });
  }

  if (usedCollaborationCredits > 0) {
    checkoutItems.push({
      label: "Collaboration Credits applied",
      amount: -(usedCollaborationCredits * collaborationCharges),
    });
  }

  const handlePaymentMethodChange = (value: "card" | "upi") => {
    setSelectedPaymentMethod(value);
    if (value === "card") {
      setFinalAmount(
        parseFloat(
          selectedPaymentFormat === "partial" ? partialPayment : totalAmount
        ) * 1.023
      ); // Adding 2.3% for card payments, including GST
    } else {
      setFinalAmount(
        parseFloat(
          selectedPaymentFormat === "partial" ? partialPayment : totalAmount
        )
      ); // Include GST for UPI as well
    }
  };
  return (
    <>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <h1 className="text-2xl font-bold mb-6 text-center">Checkout</h1>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* campaign details */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Campaign Details</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Campaign Amount</span>
                  <span className="font-medium">
                    ₹
                    {Amount
                      ? new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(Amount)
                      : 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Number of Influencers</span>
                  <span className="font-medium">{selectedCreatorsLength}</span>
                </div>
                <div className="flex justify-between">
                  <span>Collaborations Count</span>
                  <span className="font-medium">{selectedCreatorsLength}</span>
                </div>
                <div className="flex justify-between">
                  <span>Collaboration Charges (per unit)</span>
                  <span className="font-medium">
                    ₹{collaborationCharges?.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* available credits */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Available Credits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <Label htmlFor="collaboration-credits" className="mb-2 block">
                    Collaboration Credits
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="collaboration-credits"
                      type="number"
                      min="0"
                      max={AllCredits.collaborationCredits}
                      value={usedCollaborationCredits}
                      onChange={(e) =>
                        handleCreditsChange("collaboration", e.target.value)
                      }
                      className="w-20 text-sm"
                    />
                    <span className="text-muted-foreground">
                      of {AllCredits?.collaborationCredits} available
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Balance after use:{" "}
                    {AllCredits?.collaborationCredits -
                      usedCollaborationCredits}{" "}
                    credits
                  </div>
                </div>
                <Separator />
                <div>
                  <Label htmlFor="anchors-credits" className="mb-2 block">
                    Anchors Credits
                  </Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="anchors-credits"
                      type="number"
                      min="0"
                      max={AllCredits?.anchorsCredits}
                      value={usedAnchorsCredits}
                      onChange={(e) =>
                        handleCreditsChange("anchors", e.target.value)
                      }
                      className="w-20 text-sm"
                    />
                    <span className="text-muted-foreground">
                      of {AllCredits?.anchorsCredits} available
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Balance after use:{" "}
                    {AllCredits?.anchorsCredits - usedAnchorsCredits} credits
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* coupons */}
            {allowCoupons && (
              <Card className="shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Coupon</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="has-coupon"
                      checked={openCoupon}
                      onCheckedChange={setOpenCoupon}
                    />
                    <Label htmlFor="has-coupon">I have a coupon code</Label>
                  </div>
                  {openCoupon && (
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <Input
                          type="text"
                          placeholder="Enter coupon code"
                          value={coupon}
                          onChange={(e) => {
                            setCoupon(e.target.value);
                            setAppliedCoup(false);
                            if (originalPriceBeforeCoupon <= 0) {
                              setOriginalPriceBeforeCoupon(Amount);
                            }
                            if (originalPriceBeforeCoupon > 0) {
                              setPreviewData((prev) => {
                                return {
                                  ...prev,
                                  ExpectedSpendAmount:
                                    originalPriceBeforeCoupon,
                                };
                              });
                            }
                          }}
                          className="flex-grow text-sm uppercase "
                        />
                        <Button
                          disabled={appliedCoup}
                          className="text-[16px] border border-[#FF5C5C] text-[#FF5C5C] py-[10px] px-5 rounded-lg"
                          onClick={onCouponSubmit}
                          variant="outline"
                          size="sm"
                        >
                          Apply
                        </Button>
                      </div>
                      {appliedCoup && (
                        <p className="text-green-600 text-xs">
                          Coupon code "{coupon?.toUpperCase()}" applied: ₹
                          {(originalPriceBeforeCoupon - Amount)?.toFixed(2)}{" "}
                          discount
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            {/* order summary */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {checkoutItems.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span className={item.amount < 0 ? "text-green-600" : ""}>
                      {item.label}
                    </span>
                    <span
                      className={
                        item.amount < 0 ? "text-green-600" : "font-medium"
                      }
                    >
                      ₹{Math.abs(item.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
                <Separator />
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-medium">
                    ₹
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(Amount + additionalAmounts)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>GST (18%)</span>
                  <span className="font-medium">
                    ₹
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(gstAmount))}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-base font-bold">
                  <span>Total</span>
                  <span>
                    ₹
                    {totalAmount
                      ? new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(parseFloat(totalAmount))
                      : 0}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* payment options */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Payment Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Tabs
                  defaultValue="full"
                  onValueChange={(value) =>
                    setSelectedPaymentFormat(value as "partial" | "full")
                  }
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="partial">Partial (10%)</TabsTrigger>
                    <TabsTrigger value="full">Full Amount</TabsTrigger>
                  </TabsList>
                  <TabsContent value="partial" className="mt-2">
                    <p className="text-xs text-muted-foreground">
                      Pay 10% now to initiate your campaign. The remaining
                      amount will be due before campaign launch.
                    </p>
                    <p className="mt-2 text-sm font-medium">
                      Amount to pay now: ₹{" "}
                      {partialPayment
                        ? new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(parseFloat(partialPayment))
                        : 0}
                    </p>
                    <NoActivePlanAlertButton
                      Button={
                        <Button
                          disabled={selectedCreatorsLength === 0}
                          className="w-full mt-5 "
                        >
                          Pay ₹{" "}
                          {partialPayment
                            ? new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(parseFloat(partialPayment))
                            : 0}
                        </Button>
                      }
                      onClick={() => {
                        mixpanel.track(
                          "Brand Draft Campaign - CheckOut Section: Pay Partial - clicked"
                        );
                        setSelectedPaymentMethod(null);
                        setShowPaymentPopup(true);
                      }}
                    />
                  </TabsContent>
                  <TabsContent value="full" className="mt-2">
                    <p className="text-xs text-muted-foreground">
                      Pay in full now for seamless campaign launch. 10%
                      cancellation fee applies if cancelled.
                    </p>
                    <p className="mt-2 text-sm font-medium">
                      Amount to pay: ₹{" "}
                      {totalAmount
                        ? new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(parseFloat(totalAmount))
                        : 0}
                    </p>
                    <NoActivePlanAlertButton
                      Button={
                        <Button
                          disabled={selectedCreatorsLength === 0}
                          className="w-full mt-5"
                        >
                          Pay ₹{" "}
                          {totalAmount
                            ? new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(parseFloat(totalAmount))
                            : 0}
                        </Button>
                      }
                      onClick={() => {
                        mixpanel.track(
                          "Brand Draft Campaign - CheckOut Section: Pay Partial - clicked"
                        );
                        setSelectedPaymentMethod(null);
                        setShowPaymentPopup(true);
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
              <CardFooter></CardFooter>
            </Card>
          </div>
        </div>
      </div>
      <Dialog open={showPaymentPopup} onOpenChange={setShowPaymentPopup}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Select Payment Method</DialogTitle>
            <DialogDescription>
              Choose your preferred payment method to complete the transaction.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RadioGroup
              onValueChange={handlePaymentMethodChange}
              className="space-y-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="card" id="card" />
                <Label htmlFor="card">
                  Credit Card, Debit Card, Net Banking, Wallet (2.3% extra fee,
                  incl. GST)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="upi" id="upi" />
                <Label htmlFor="upi">UPI (No extra fee)</Label>
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            {selectedPaymentMethod && (
              <Button
                onClick={() => {
                  mixpanel.track(
                    "Brand Draft Campaign - CheckOut Section: Pay - clicked"
                  );
                  setShowPaymentPopup(false);
                  handlePayment();
                }}
                className="w-full"
              >
                Pay ₹{finalAmount?.toFixed(2)} (incl. GST) via{" "}
                {selectedPaymentMethod === "card" ? "Card/Banking" : "UPI"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
